import { useState } from "react";
import { LayoutGrid, Users, FileText, Map, Banknote, LogOut, Menu, X, KeyRound } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import OverviewTab from "./tabs/OverviewTab";
import UsersTab from "./tabs/UsersTab";
import KycTab from "./tabs/KycTab";
import MappingTab from "./tabs/MappingTab";
import WithdrawalsTab from "./tabs/WithdrawalsTab";
import ChangePasswordModal from "./ChangePasswordModal";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutGrid, comp: OverviewTab },
  { key: "users", label: "Users & Devices", icon: Users, comp: UsersTab },
  { key: "kyc", label: "KYC Approvals", icon: FileText, comp: KycTab },
  { key: "mapping", label: "Road Mapping", icon: Map, comp: MappingTab },
  { key: "withdrawals", label: "Withdrawals", icon: Banknote, comp: WithdrawalsTab },
];

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const [active, setActive] = useState("overview");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const ActiveComp = TABS.find((t) => t.key === active)?.comp || OverviewTab;
  const initial = (admin?.email || "A").charAt(0).toUpperCase();

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#0A192F] text-slate-100 antialiased flex">
      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-40 h-screen w-64 shrink-0 flex flex-col border-r border-white/10 bg-[#0A192F] transition-transform`}
      >
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
          <img src="/geonetix-logo-full.webp" alt="GeoNetix" className="h-10 w-auto object-contain" />
          <div>
            <div className="font-display font-black text-base leading-tight">GeoNetix</div>
            <div className="text-[10px] font-mono-gx uppercase tracking-[0.22em] text-[#00B4D8]">Admin</div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.key === active;
            return (
              <button
                key={t.key}
                data-testid={`admin-nav-${t.key}`}
                onClick={() => {
                  setActive(t.key);
                  setMobileOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-[#00B4D8]/10 text-[#00B4D8] border border-[#00B4D8]/30"
                    : "text-slate-300 hover:bg-white/5"
                }`}
              >
                <Icon size={16} />
                {t.label}
              </button>
            );
          })}
        </nav>

        <div className="px-4 pb-4 border-t border-white/10 pt-4">
          <div className="text-xs text-slate-400 mb-3 truncate" title={admin?.email}>
            {admin?.email || "GeoNetix Admin"}
          </div>
          <button
            data-testid="admin-change-password"
            onClick={() => setShowPwd(true)}
            className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-slate-200 hover:bg-white/5 transition-colors mb-1"
          >
            <KeyRound size={15} /> Change password
          </button>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-red-300 hover:bg-red-400/10 transition-colors"
          >
            <LogOut size={15} /> Logout
          </button>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 bg-[#0A192F]/90 backdrop-blur border-b border-white/10">
          <div className="px-5 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                data-testid="admin-mobile-menu"
                onClick={() => setMobileOpen((v) => !v)}
                className="lg:hidden text-slate-300 hover:text-white"
                aria-label="Toggle sidebar"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              <h1 data-testid="admin-page-title" className="font-display font-black text-xl tracking-tight">
                {TABS.find((t) => t.key === active)?.label}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <div
                data-testid="admin-avatar"
                className="w-10 h-10 rounded-full bg-[#4CAF50] text-[#04140a] font-black flex items-center justify-center"
                title={admin?.email}
              >
                {initial}
              </div>
            </div>
          </div>
        </header>

        <main data-testid="admin-content" className="p-5 lg:p-8">
          <ActiveComp onNavigate={setActive} />
        </main>
      </div>

      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}
    </div>
  );
}
